import { useState, useEffect } from 'react';
import { adminApi } from '../../api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Wallet, Check, X, Loader2, Image, ArrowRight, Search } from 'lucide-react';
import Pagination from '../../components/Pagination';

const formatMoney = (amount) => new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', minimumFractionDigits: 0 }).format(amount);
const formatDate = (date) => new Date(date).toLocaleDateString('en-NG', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

const PER_PAGE = 15;

export default function AdminDeposits() {
  const [deposits, setDeposits] = useState([]);
  const [allDeposits, setAllDeposits] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [approveAmount, setApproveAmount] = useState('');
  const [note, setNote] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [viewAll, setViewAll] = useState(false);

  useEffect(() => { fetchDeposits(); }, [filter]);

  useEffect(() => {
    let filtered = allDeposits;
    
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = allDeposits.filter(deposit => 
        deposit.username?.toLowerCase().includes(query) || 
        deposit.email?.toLowerCase().includes(query)
      );
    }
    
    setDeposits(filtered);
    setCurrentPage(1);
  }, [searchQuery, allDeposits]);

  const fetchDeposits = async () => {
    setIsLoading(true);
    try { 
      const response = await adminApi.getDeposits(filter === 'all' ? undefined : filter); 
      setAllDeposits(response.data || []);
      setDeposits(response.data || []);
    } catch (error) { console.error('Failed to fetch deposits:', error); } 
    finally { setIsLoading(false); }
  };

  const handleApprove = async (deposit) => {
    try { 
      const amountToCredit = approveAmount ? parseFloat(approveAmount) : (deposit.credited_amount || deposit.amount);
      await adminApi.approveDeposit(deposit.id, amountToCredit, note || undefined); 
      toast.success(`Deposit approved - ${formatMoney(amountToCredit)} credited`); 
      setApproveAmount(''); 
      setNote(''); 
      fetchDeposits(); 
    }
    catch (error) { toast.error('Failed to approve'); }
  };

  const handleReject = async (deposit) => {
    try { await adminApi.rejectDeposit(deposit.id, note || 'Rejected by admin'); toast.success('Deposit rejected'); setNote(''); fetchDeposits(); }
    catch (error) { toast.error('Failed to reject'); }
  };

  // Pagination logic
  const totalItems = deposits.length;
  const totalPages = viewAll ? 1 : Math.ceil(totalItems / PER_PAGE);
  const displayedDeposits = viewAll 
    ? deposits 
    : deposits.slice((currentPage - 1) * PER_PAGE, currentPage * PER_PAGE);

  if (isLoading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#FFD700]" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <h1 className="text-2xl font-bold text-white font-['Outfit']">Deposits ({totalItems})</h1>
        
        {/* Search Bar */}
        <div className="relative w-full sm:w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input
            placeholder="Search by username..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-[#121212] border-white/10 text-white placeholder:text-zinc-500"
          />
        </div>
      </div>

      <Tabs value={filter} onValueChange={(v) => { setFilter(v); setCurrentPage(1); setViewAll(false); }}>
        <TabsList className="bg-[#0A0A0A] border border-white/10">
          <TabsTrigger value="pending">Pending</TabsTrigger>
          <TabsTrigger value="approved">Approved</TabsTrigger>
          <TabsTrigger value="rejected">Rejected</TabsTrigger>
          <TabsTrigger value="all">All</TabsTrigger>
        </TabsList>
      </Tabs>

      {viewAll && totalItems > PER_PAGE && (
        <div className="flex items-center justify-between bg-[#FFD700]/10 border border-[#FFD700]/30 rounded-lg p-3">
          <span className="text-sm text-[#FFD700]">Viewing all {totalItems} deposits</span>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => { setViewAll(false); setCurrentPage(1); }}
            className="border-[#FFD700]/30 text-[#FFD700] hover:bg-[#FFD700]/10"
          >
            Show Paginated
          </Button>
        </div>
      )}

      {deposits.length === 0 ? (
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardContent className="p-8 text-center">
            <Wallet className="w-12 h-12 mx-auto text-zinc-600 mb-4" />
            <p className="text-zinc-400">{searchQuery ? `No deposits found matching "${searchQuery}"` : 'No deposits'}</p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="space-y-4">
            {displayedDeposits.map((deposit) => (
              <Card key={deposit.id} className="bg-[#0A0A0A] border-white/10 hover:border-[#FFD700]/30 transition-colors">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-[#121212] rounded-lg flex items-center justify-center"><Wallet className="w-5 h-5 text-[#FFD700]" /></div>
                      <div>
                        <p className="font-semibold text-white">{deposit.username}</p>
                        <p className="text-sm text-zinc-500">{deposit.email} · {formatDate(deposit.created_at)}</p>
                        <p className="text-sm text-zinc-400">Account: {deposit.payment_account_name}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-2xl font-bold text-white">{formatMoney(deposit.amount)}</p>
                        {deposit.fee > 0 && (
                          <div className="flex items-center gap-2 text-sm">
                            <span className="text-red-400">-{formatMoney(deposit.fee)} fee</span>
                            <ArrowRight className="w-3 h-3 text-zinc-500" />
                            <span className="text-[#FFD700] font-semibold">{formatMoney(deposit.credited_amount || deposit.amount)}</span>
                          </div>
                        )}
                      </div>
                      <Badge variant="outline" className={deposit.status === 'approved' ? 'border-green-500/30 text-green-500' : deposit.status === 'rejected' ? 'border-red-500/30 text-red-500' : 'border-yellow-500/30 text-yellow-500'}>{deposit.status}</Badge>
                    </div>
                  </div>
                  {deposit.receipt_image && (
                    <Dialog>
                      <DialogTrigger asChild><Button variant="outline" size="sm" className="mt-4 border-white/10"><Image className="w-4 h-4 mr-2" />View Receipt</Button></DialogTrigger>
                      <DialogContent className="bg-[#0A0A0A] border-white/10 max-w-lg"><img src={`${process.env.REACT_APP_BACKEND_URL}${deposit.receipt_image}`} alt="Receipt" className="w-full rounded" /></DialogContent>
                    </Dialog>
                  )}
                  {deposit.status === 'pending' && (
                    <div className="mt-4 pt-4 border-t border-white/10 flex flex-wrap gap-4 items-end">
                      <div className="flex-1 min-w-[150px]">
                        <label className="text-xs text-zinc-500">Credit Amount (default: {formatMoney(deposit.credited_amount || deposit.amount)})</label>
                        <Input type="number" placeholder={(deposit.credited_amount || deposit.amount).toString()} value={approveAmount} onChange={(e) => setApproveAmount(e.target.value)} className="bg-[#121212] border-white/10 text-white" />
                      </div>
                      <div className="flex-1 min-w-[150px]">
                        <label className="text-xs text-zinc-500">Note (optional)</label>
                        <Input placeholder="Add note" value={note} onChange={(e) => setNote(e.target.value)} className="bg-[#121212] border-white/10 text-white" />
                      </div>
                      <Button onClick={() => handleApprove(deposit)} className="bg-green-600 hover:bg-green-700"><Check className="w-4 h-4 mr-2" />Approve</Button>
                      <Button onClick={() => handleReject(deposit)} variant="destructive"><X className="w-4 h-4 mr-2" />Reject</Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Pagination */}
          {!viewAll && totalPages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalItems}
              perPage={PER_PAGE}
              onPageChange={setCurrentPage}
              onViewAll={() => setViewAll(true)}
            />
          )}
        </>
      )}
    </div>
  );
}
